import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { productCategories } from "../../mocks/products";
import { Link } from "react-router-dom";

// Add a lookup for dedicated landing page URLs
const landingPageMap: Record<string, string> = {
  "bifold-doors": "/bifold-doors",
  "roof-lanterns": "/roof-lanterns",
  "composite-doors": "/composite-doors",
  "conservatories": "/conservatories",
  "upvc-windows": "/windows",
  "aluminium-windows": "/windows",
};

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { id: "all", label: "All Products" },
    { id: "doors", label: "Doors" },
    { id: "windows", label: "Windows" },
    { id: "extensions", label: "Extensions" },
  ];

  const doorsIds = ["bifold-doors", "composite-doors", "french-doors", "patio-doors"];
  const windowsIds = ["upvc-windows", "aluminium-windows"];
  const extensionsIds = ["conservatories", "roof-lanterns"];

  const filtered =
    activeTab === "all"
      ? productCategories
      : activeTab === "doors"
      ? productCategories.filter((p) => doorsIds.includes(p.id))
      : activeTab === "windows"
      ? productCategories.filter((p) => windowsIds.includes(p.id))
      : productCategories.filter((p) => extensionsIds.includes(p.id));

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/fa215eb4f8ac62272f85418d55ce140a.jpg"
            alt="Products & Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-zinc-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>What We Install</span>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mt-4 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Products &amp; Services
          </h1>
          <p className="text-stone-300 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            From slim aluminium bifold doors to bespoke conservatories — every product is made to measure and installed by our expert team.
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8 bg-white border-b border-stone-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "bg-amber-600 text-white"
                  : "bg-stone-100 text-zinc-600 hover:bg-stone-200"
              }`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-20">
          {filtered.map((product, idx) => (
            <article key={product.id} id={product.slug} className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 !== 0 ? "lg:[direction:rtl]" : ""}`}>
              <div className={`w-full h-[420px] rounded-lg overflow-hidden ${idx % 2 !== 0 ? "[direction:ltr]" : ""}`}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className={idx % 2 !== 0 ? "[direction:ltr]" : ""}>
                <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {product.tagline}
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-zinc-900 mt-3 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {product.name}
                </h2>
                <p className="text-zinc-500 text-base leading-relaxed mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {product.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-amber-600"></i>
                      </div>
                      <span className="text-zinc-600 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  Get a Quote
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
                </Link>
                {landingPageMap[product.id] && (
                  <Link
                    to={landingPageMap[product.id]}
                    className="inline-flex items-center gap-2 border-2 border-zinc-300 hover:border-zinc-800 text-zinc-600 hover:text-zinc-900 font-semibold px-6 py-3 rounded-md transition-all whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: "'Raleway', sans-serif" }}
                  >
                    Full Details
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line"></i></div>
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Not Sure Which Product Is Right for You?
          </h3>
          <p className="text-stone-300 mb-8 max-w-lg mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Our expert team is happy to advise. Call us or book a free home survey and we will help you choose the perfect solution.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:07852636300" className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-3 rounded-md whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Call 07852 636 300
            </a>
            <Link to="/contact" className="border border-white/40 hover:border-white text-white font-semibold px-8 py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Book a Free Survey
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
